
import { GeofenceType } from "@enums/GeofenceType";
import { IGeofence } from "@interfaces/IGeofences";
import { CanvasService } from "@services/canvas.service";
import * as angular from "angular";
import { IGeofenceNavigationPoint } from "./geofences.interfaces";

export interface IGeofenceOverlaySize {
    sw?: google.maps.Point;
    ne?: google.maps.Point;
    width?: number;
    height?: number;
}


export class GeofenceOverlay {

    overlay: google.maps.OverlayView;
    bounds: google.maps.LatLngBounds;
    visible = false;

    private canvas: HTMLCanvasElement = null;
    private div: HTMLDivElement = null;
    private context: CanvasRenderingContext2D;
    private overlayProjection: google.maps.MapCanvasProjection;
    private descriptionLines: string[];
    private longestLine: string;
    private className = "geofenceOverlay";
    private previousClassname;
    private hover = false;

    private backgroundColor: string;
    private strokeStyle: string;
    private isReadable = false;
    private navigationPointMarkerZoomThreshold = 15;
    private hasAddedNavigationPoints = false;

    private previousSize: IGeofenceOverlaySize = {};

    constructor(
        public geofence: IGeofence,
        private map: google.maps.Map,
        private showLabelWhenZoomedOut: boolean,
        private drawOnAdd = false) {

        performance.mark("GCreation");

        this.canvas = null;
        this.div = null;

        this.init();
        performance.measure("GCreation");
    }

    removeChild() {
        angular.element(this.div).remove();
    }

    hideElement() {
        if (this.div && this.visible) {
            this.visible = false;
            this.overlay.setMap(null);
        }
    }

    showElement() {
        if (!this.div) {
            this.initOverlay();

            this.descriptionLines = this.splitGeofenceName();
            this.longestLine = this.getLongestLine(this.descriptionLines);

            this.createElements();
        }

        if (this.div && !this.visible) {
            this.visible = true;
            this.overlay.setMap(this.map);
        }
    }

    setGeofenceClass(forceShowLabel: boolean = false) {
        const showLabel = (forceShowLabel || this.showLabelWhenZoomedOut) && !this.isReadable;

        let className = this.className;

        if (this.hover) {
            className += " hover";
        }

        if (showLabel) {
            className += ` showLabel`;

            if (forceShowLabel) {
                className += ` force`;
            }
        }

        if (className != this.previousClassname) {
            this.div.className = className;
            this.previousClassname = className;
        }
    }

    setHover(hover: boolean) {
        if (this.hover === hover) {
            return;
        }

        this.hover = hover;

        this.updateColoursForMapType();

        this.hideElement();
        this.showElement();

        if (this.visible) {

            // Quick hack to clear canvas
            this.canvas.width = this.canvas.width;
            this.drawGeofence(true);
        }
    }

    updateColoursForMapType() {
      /*  if (this.hover) {
            // red
            this.backgroundColor = "rgba(250, 100, 100, 0.4)";
            this.strokeStyle = "rgba(250, 100, 100, 0.8)";

            // Blue
            this.backgroundColor = "rgba(33,150,243,0.4)";
            this.strokeStyle = "rgba(33,150,243,0.9)";
        }
        else*/ if (this.map.getMapTypeId() === google.maps.MapTypeId.SATELLITE || this.map.getMapTypeId() === google.maps.MapTypeId.HYBRID) {
            this.backgroundColor = "rgba(250, 250, 250, 0.4)";
            this.strokeStyle = "rgba(50, 50, 50, 0.8)";
        }
        else {
            this.backgroundColor = "rgba(50, 50, 50, 0.4)";
            this.strokeStyle = "rgba(50, 50, 50, 0.8)";
        }

        //var random = this.randomNumber(0, geofenceColors.length - 1);
        //this.strokeStyle = geofenceColors[random];
        //this.backgroundColor = this.createTransparentColor(this.strokeStyle, 0.4);

        if (this.geofence.PriorityTag && this.geofence.PriorityTag.ColourObject) {
            this.backgroundColor = this.geofence.PriorityTag.ColourObject.transparentColour;
            this.strokeStyle = this.geofence.PriorityTag.ColourObject.solidColour;
        }
    }

    getOverlaySize(): IGeofenceOverlaySize {
        if (!this.overlayProjection) {
            return {};
        }

        const sw = this.overlayProjection.fromLatLngToDivPixel(this.bounds.getSouthWest());
        const ne = this.overlayProjection.fromLatLngToDivPixel(this.bounds.getNorthEast());

        const width = Math.round(ne.x - sw.x);
        const height = Math.round(sw.y - ne.y);

        return {
            sw: sw,
            ne: ne,
            width: width,
            height: height
        }
    }

    drawGeofence(forceRedraw = false) {
        //var t1 = performance.now();

        //var map = this.overlay.getMap() as google.maps.Map;
        if (!this.overlay || !this.overlay.getMap()) {
            return;
        }

        window.performance.mark("drawGeofence");

        const hasResized = this.resizeContainingElement();
        if (!forceRedraw && !hasResized) {
            return;
        }

        if (forceRedraw) {
            this.context.clearRect(0, 0, this.previousSize.width, this.previousSize.height);
        }

        this.context.strokeStyle = this.strokeStyle;
        this.context.lineWidth = this.hover ? 3 : 1;
        this.context.fillStyle = this.backgroundColor;

        this.setGeofenceClass();

        if (this.geofence.GeofenceType === GeofenceType.Circle) {
            this.drawCircleGeofence(this.isReadable);
        }
        else {
            this.drawPolygonGeofence(this.isReadable);
        }

        window.performance.measure("drawGeofence");
    }

    // ---

    private init() {
        if (this.geofence.GeofenceType === GeofenceType.Circle) {
            const location = new google.maps.LatLng(this.geofence.Latitude, this.geofence.Longitude);
            this.bounds = this.getBoundingBoxForCircle(location, this.geofence.Radius);
        }
        else {
            this.bounds = this.getBoundingBoxForPolygonArray(this.geofence.Points);
        }

        if (this.drawOnAdd) {
            this.showElement();
        }
    }

    private initOverlay() {
        this.overlay = new google.maps.OverlayView();

        this.overlay.onAdd = () => {
            this.addOverlayElements();

            if (this.drawOnAdd) {
                //this.overlay.setMap(this.map);
                this.showElement();
                this.drawGeofence();
            }
        };

        this.overlay.draw = () => {
            this.drawGeofence();
        }

        this.overlay.onRemove = () => {
            this.removeChild();
        }
    }

    private randomNumber(min: number, max: number) {
        return Math.floor(Math.random() * (max - min + 1) + min);
    }

    private createElements() {

        window.performance.mark("createElements");
        this.div = document.createElement("div") as HTMLDivElement;
        this.div.className = this.className;

        if (this.showLabelWhenZoomedOut) {
            const textElement = document.createElement("div");
            textElement.className = "description";
            textElement.textContent = "";

            this.descriptionLines.forEach(line => {
                textElement.innerHTML += `${line}<br />`;
            });

            this.div.appendChild(textElement);
        }

        this.canvas = document.createElement("canvas") as HTMLCanvasElement;
        this.context = this.canvas.getContext("2d");

        this.div.appendChild(this.canvas);



        window.performance.measure("createElements");
    }

    private removeCurrentNavigationPointElements(): void {
        const existingElements = this.div.getElementsByClassName(`navigation-point`);
        while (existingElements?.length > 0) {
            this.div.removeChild(existingElements[0]);
        }

        this.hasAddedNavigationPoints = false;
    }

    private createNavigationPointElements(navigationPoints: IGeofenceNavigationPoint[], div: HTMLDivElement): void {
        if (!navigationPoints || !div) {
            return;
        }

        const shouldShowNavigationPoints = this.map.getZoom() > this.navigationPointMarkerZoomThreshold;

        if (shouldShowNavigationPoints && this.hasAddedNavigationPoints) {
            return;
        }
        else if (!shouldShowNavigationPoints && this.hasAddedNavigationPoints) {
            this.removeCurrentNavigationPointElements();
        }
        else if (shouldShowNavigationPoints) {
            var totalWidth = this.bounds.getNorthEast().lng() - this.bounds.getSouthWest().lng();
            var totalHeight = this.bounds.getNorthEast().lat() - this.bounds.getSouthWest().lat();

            navigationPoints?.forEach(navigationPoint => {
                const elementClass = `navigation-point`;
                const elementId = elementClass + `-${navigationPoint?.Id}`;
                const point = this.convertLatLngToPixelCoordinates([navigationPoint.Latitude, navigationPoint.Longitude], totalWidth, totalHeight);

                const newDiv: HTMLDivElement = document.createElement("div");
                newDiv.style.left = `${point[0] * 100 / this.previousSize.width}%`;
                newDiv.style.top = `${point[1] * 100 / this.previousSize.height}%`;

                newDiv.id = elementId;
                newDiv.className = elementClass;
                newDiv.textContent = ``;
                newDiv.innerHTML += `<i class="material-icons">location_on</i><div class="label">${navigationPoint?.Name}</div>`;

                div.appendChild(newDiv);
            });

            this.hasAddedNavigationPoints = true;
        }
    }

    private getBoundingBoxForCircle(point: google.maps.LatLng, radius: number) {
        const north = google.maps.geometry.spherical.computeOffset(point, radius, 0);
        const east = google.maps.geometry.spherical.computeOffset(point, radius, 90);
        const south = google.maps.geometry.spherical.computeOffset(point, radius, 180);
        const west = google.maps.geometry.spherical.computeOffset(point, radius, 270);

        const bounds = new google.maps.LatLngBounds();

        bounds.extend(north);
        bounds.extend(east);
        bounds.extend(south);
        bounds.extend(west);

        return bounds;
    }

    private getBoundingBoxForPolygonArray(polygon: [number, number][]) {
        const bounds = new google.maps.LatLngBounds();

        for (let i = 0; i < polygon.length; i++) {
            const latLng = new google.maps.LatLng(polygon[i][0], polygon[i][1]);
            bounds.extend(latLng);
        }

        return bounds;
    }

    private addOverlayElements() {
        if (!this.div) {
            this.createElements();
        }

        const panes = this.overlay.getPanes();
        panes.overlayLayer.appendChild(this.div);

        // We use the south-west and north-east coordinates of the overlay to peg it to the correct position and size.
        // To do this, we need to retrieve the projection from the overlay.
        this.overlayProjection = this.overlay.getProjection();

        //if (this.geofence.Points) {
        //    const plabel = new PolyLabel();
        //    const center = plabel.getCenter([this.geofence.Points]);

        //    this.geofence.Latitude = center[0];
        //    this.geofence.Longitude = center[1];
        //}

        this.updateColoursForMapType();
    }

    private drawPolygonGeofence(drawText: boolean) {
        var totalWidth = this.bounds.getNorthEast().lng() - this.bounds.getSouthWest().lng();
        var totalHeight = this.bounds.getNorthEast().lat() - this.bounds.getSouthWest().lat();

        this.context.beginPath();
        for (var i = 0; i < this.geofence.Points.length; i++) {
            const pixel = this.convertLatLngToPixelCoordinates(this.geofence.Points[i], totalWidth, totalHeight);

            this.context.lineTo(pixel[0], pixel[1]);
        }

        //this.context.closePath();
        this.context.stroke();
        this.context.fill();

        if (drawText) {
            var middleLng = (this.bounds.getNorthEast().lng() + this.bounds.getSouthWest().lng()) / 2;

            const pixel = this.convertLatLngToPixelCoordinates([this.geofence.Latitude, middleLng], totalWidth, totalHeight);
            this.fillText(pixel[0], pixel[1]);
        }
    }

    private convertLatLngToPixelCoordinates(p: [number, number], totalWidth: number, totalHeight: number) {
        const mappedXpercent = (p[1] - this.bounds.getSouthWest().lng()) / totalWidth;
        const mappedYpercent = (p[0] - this.bounds.getSouthWest().lat()) / totalHeight;

        // Whole numbers are far faster for the browser to render
        const pixelX = Math.round(this.previousSize.width * mappedXpercent);
        const pixelY = Math.round(this.previousSize.height - (this.previousSize.height * mappedYpercent));

        return [pixelX, pixelY];
    }

    private drawCircleGeofence(drawText: boolean) {
        if (this.previousSize.width <= this.context.lineWidth * 2) {
            return;
        }

        const centerX = this.previousSize.width / 2;
        const centerY = this.previousSize.height / 2;

        // Check we're trying to draw a valid circle.
        const radius = centerX - Math.ceil(this.context.lineWidth / 4);

        if (radius <= 0) {
            console.warn("no radius", radius, centerX, this.context.lineWidth, this.previousSize.width, this.geofence);
            return;
        }

        this.drawCircle(centerX, centerY, radius);

        if (drawText) {
            this.fillText(centerX, centerY);
        }
    }

    private resizeContainingElement(): boolean {
        const size = this.getOverlaySize();

        if (!size || !size.sw || !size.ne) {
            return false;
        }

        if (this.previousSize.ne &&
            this.previousSize.sw &&
            this.previousSize.ne.equals(size.ne) &&
            this.previousSize.sw.equals(size.sw)) {
            return false;
        }

        this.div.style.left = size.sw.x + "px";
        this.div.style.top = size.ne.y + "px";

        this.previousSize.ne = size.ne;
        this.previousSize.sw = size.sw;

        if (size.width == this.previousSize.width && size.height == this.previousSize.height) {
            return false;
        }

        this.div.style.width = size.width + "px";
        this.div.style.height = size.height + "px";

        this.canvas.width = size.width;
        this.canvas.height = size.height;

        this.previousSize = size;

        this.isReadable = this.isGeofenceTextReadable();

        this.createNavigationPointElements(this.geofence?.navigationPoints, this.div);

        return true;
    }

    private drawCircle(x: number, y: number, radius: number, backgroundColor: string = this.backgroundColor) {
        window.performance.mark("drawCircle");

        this.context.beginPath();
        this.context.arc(x, y, radius, 0, 2 * Math.PI, false);
        this.context.closePath();
        //this.context.lineWidth = this.strokeWidth;
        this.context.fillStyle = (backgroundColor) ? backgroundColor : "rgba(250, 250, 250, 0.4)";

        this.context.stroke();
        this.context.fill();


        window.performance.measure("drawCircle");
    }

    private isGeofenceTextReadable() {
        const fontSize = this.getFontSizeForLine(this.longestLine);
        return fontSize >= 7;
    }

    private fillText(x: number, y: number) {
        window.performance.mark("fillText");

        this.context.fillStyle = "black";
        this.context.textAlign = "center";

        const fontSize = this.getFontSizeForLine(this.longestLine);
        this.context.font = `${fontSize}px Roboto`;

        if (this.descriptionLines.length === 1) {
            this.context.textBaseline = "middle";
            this.context.fillText(this.descriptionLines[0], x, y);
        }
        else {
            this.context.textBaseline = "bottom";
            this.context.fillText(this.descriptionLines[0], x, y);
            this.context.textBaseline = "top";
            this.context.fillText(this.descriptionLines[1], x, y);
        }

        window.performance.measure("fillText");
    }

    private reduceLineCount(lines: string[], includeMidPoint = false) {
        const lineArray: string[] = ["", ""];

        let midPoint = Math.floor(lines.length / 2);

        if (includeMidPoint) {
            midPoint++;
        }

        for (let i = 0; i < lines.length; i++) {
            const index = i < midPoint ? 0 : 1;

            let line = lineArray[index] || "";

            if (line.length > 0) {
                line += " ";
            }

            line += lines[i];
            lineArray[index] = line;
        }

        return lineArray;
    }

    private splitGeofenceName() {

        if (!this.geofence.Description || this.geofence.Description.length === 0) {
            return [];
        }

        // aim for a maximum of two lines, split near middle if a space exists
        const lines = this.geofence.Description.trim().toUpperCase().split(" ");
        if (lines.length <= 2) {

            return lines;
        }

        // If we have more than one line then lets look for the most appropriate combination of words
        const lineArray1 = this.reduceLineCount(lines, false);

        // If we have even lines this should be fairly easy
        if (lines.length % 2 === 0) {
            return lineArray1;
        }

        const lineArray2 = this.reduceLineCount(lines, true);

        const lineArray1Diff = Math.abs(lineArray1[0].length - lineArray1[1].length);
        const lineArray2Diff = Math.abs(lineArray2[0].length - lineArray2[1].length);

        return lineArray1Diff < lineArray2Diff ? lineArray1 : lineArray2;
    }

    private getLongestLine(lines: string[]) {
        var longestLine = "";

        lines.forEach((line) => {
            if (line.length > longestLine.length) {
                longestLine = line;
            }
        });

        return longestLine;
    }

    private getFontSizeForLine(line: string) {
        window.performance.mark("getFontSizeForLine");

        const fractionModifier = line.length < 5 ? 1 : 1.2;
        const letterFraction = fractionModifier / line.length;
        let pixelSize = Math.min(this.previousSize.width, this.previousSize.height) * letterFraction;

        // Makes wider polygons text bigger, works best on boxes rather than irregular polygon
        const widthModifier = 0.75;
        if (this.previousSize.width * widthModifier > this.previousSize.height) {
            pixelSize *= this.previousSize.width * widthModifier / this.previousSize.height;
        }

        // Set max size
        pixelSize = Math.min(pixelSize, 60);

        window.performance.measure("getFontSizeForLine");

        return pixelSize;
    }

    private addLabel(x: number, y: number, text: string) {
        window.performance.mark("addLabel");

        this.context.fillStyle = "black";
        this.context.textAlign = "center";

        const fontSize = this.getFontSizeForLine(text);
        this.context.font = `${fontSize / 3}px Roboto`;
        this.context.textBaseline = "middle";
        this.context.fillText(text, x, y);

        window.performance.measure("addLabel");
    }
}
